import type { IconName } from '../ui/Icon'
import { env, type AppEnv } from '../config/env'

/**
 * Разделы админки — порядок и названия прототипа
 * (03_dorixona_monorepo/docs/ui/admin.html › MODULES). Пункт появляется в
 * меню, только если у роли есть `r` на этот модуль.
 */
export interface AdminModuleItem {
  id: string
  name: string
  icon: IconName
  path: string
  /** Раздел новой функции: виден, только пока включён её флаг в env. */
  feature?: keyof AppEnv['features']
  /**
   * false — раздела нет в списке меню: вход к нему отдельной строкой над
   * профилем (уведомления). Адрес, заголовок и права — как у всех.
   */
  inNav?: false
}

export const MODULES: AdminModuleItem[] = [
  { id: 'dashboard', name: 'Дашборд', icon: 'i-dash', path: '/' },
  { id: 'orders', name: 'Заказы', icon: 'i-box', path: '/orders' },
  {
    id: 'notifications',
    name: 'Уведомления',
    icon: 'i-bell',
    path: '/notifications',
    feature: 'notifications',
    inNav: false,
  },
  { id: 'products', name: 'Товары', icon: 'i-pill', path: '/products' },
  { id: 'categories', name: 'Категории', icon: 'i-tag', path: '/categories' },
  { id: 'stories', name: 'Сторис', icon: 'i-stories', path: '/stories', feature: 'stories' },
  { id: 'news', name: 'Новости', icon: 'i-news', path: '/news', feature: 'news' },
  { id: 'reviews', name: 'Отзывы', icon: 'i-star', path: '/reviews', feature: 'reviews' },
  { id: 'customers', name: 'Клиенты', icon: 'i-users', path: '/customers' },
  { id: 'stores', name: 'Аптеки', icon: 'i-pin', path: '/stores' },
  { id: 'admins', name: 'Сотрудники', icon: 'i-badge', path: '/admins' },
  { id: 'roles', name: 'Роли доступа', icon: 'i-key', path: '/roles' },
  { id: 'integrations', name: 'Интеграции', icon: 'i-plug', path: '/integrations' },
  { id: 'settings', name: 'Настройки', icon: 'i-gear', path: '/settings' },
]

/** Разделы, которые есть сейчас: раздел выключенной функции не существует вовсе. */
export function activeModules(): AdminModuleItem[] {
  return MODULES.filter((m) => m.feature === undefined || env.features[m.feature])
}

/**
 * Куда вести роль, которой текущий раздел закрыт: первый доступный ей пункт
 * меню. Не доступно вообще ничего — на дашборд, там она увидит отказ и
 * объяснение, а не пустой экран.
 */
export function firstAllowedPath(can: (module: string) => boolean): string {
  return activeModules().find((m) => can(m.id))?.path ?? '/'
}

/** Раздел, на котором мы стоим: самый длинный совпавший путь. */
export function currentModule(pathname: string): AdminModuleItem | null {
  return (
    activeModules()
      .sort((a, b) => b.path.length - a.path.length)
      .find((m) => pathname === m.path || pathname.startsWith(`${m.path}/`)) ?? null
  )
}
